import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, ScanSearch, FileWarning, FileCheck2, ShieldCheck, Clock3, FileText } from "lucide-react";
import { HeroFade, Reveal } from "../components/Motion";
import { MarqueeStrip } from "../components/MarqueeStrip";

const HERO_IMG =
  "https://images.unsplash.com/photo-1556155092-490a1ba16284?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG93bmVyJTIwd29ya2luZyUyMGxhcHRvcHxlbnwwfHx8fDE3ODc5MzI5MzV8MA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  {
    num: "01",
    icon: FileWarning,
    title: "The Problem",
    body: "In the US, private businesses keep getting sued over websites that are hard to use for people who are blind or who only use a keyboard. The owners never notice — until a demand letter arrives.",
  },
  {
    num: "02",
    icon: ScanSearch,
    title: "The Risk",
    body: "There is no federal \u201cfix by this date\u201d rule for a restaurant, clinic, or shop. The cases still happen. A cheap accessibility widget on the site does not stop them.",
  },
  {
    num: "03",
    icon: FileCheck2,
    title: "The Answer",
    body: "A clear, written report. What is broken on your public pages, what to change, delivered in 3 business days. $99 flat. You or your web person do the work.",
  },
];

const TRUST = [
  { icon: ShieldCheck, label: "Human-written reports" },
  { icon: Clock3, label: "3 business day delivery" },
  { icon: FileText, label: "You keep the report" },
];

export default function Home() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <main data-testid="home-page">
      <section className="relative overflow-hidden px-6 pb-20 pt-36 md:px-10 md:pt-44">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 top-64 h-[360px] w-[360px] rounded-full bg-blue-50 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <HeroFade delay={0.1}>
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              EY Tech LLC — Accessibility Audit Unit
            </p>
          </HeroFade>

          <h1 data-testid="hero-heading" className="max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-slate-900 md:text-7xl lg:text-8xl">
            <HeroFade delay={0.2}>Flawless digital</HeroFade>
            <HeroFade delay={0.35}>
              <em className="text-blue-600">access.</em>
            </HeroFade>
          </h1>

          <HeroFade delay={0.5} className="mt-8 max-w-xl">
            <p data-testid="hero-subtext" className="text-base leading-relaxed text-slate-600 md:text-lg">
              We read your public pages the way an auditor — and a plaintiff — would.
              Then we hand you a written report of every problem and exactly what to change.
            </p>
          </HeroFade>

          <HeroFade delay={0.65} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/report"
              data-testid="hero-cta-report"
              className="group flex items-center gap-2.5 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
            >
              Get the $99 Report
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <span className="flex items-center gap-2 text-sm font-medium text-slate-500">
              <ArrowDown className="h-4 w-4 animate-bounce" />
              Scroll
            </span>
          </HeroFade>

          <HeroFade delay={0.8} className="mt-16 flex flex-wrap gap-x-10 gap-y-4 border-t border-slate-200 pt-8">
            {TRUST.map((t) => (
              <span key={t.label} className="flex items-center gap-2.5 text-sm font-medium text-slate-600">
                <t.icon className="h-4 w-4 text-blue-600" />
                {t.label}
              </span>
            ))}
          </HeroFade>
        </div>
      </section>

      <MarqueeStrip />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <Reveal>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
            The Manifesto
          </p>
          <h2 data-testid="manifesto-heading" className="mb-16 max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
            Three things every site owner should know.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.1}>
              <article
                data-testid={`manifesto-chapter-${c.num}`}
                className="group flex h-full flex-col gap-6 rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift md:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-4xl font-medium italic text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                    {c.num}
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 transition-colors duration-300 group-hover:bg-blue-100">
                    <c.icon className="h-5 w-5 text-blue-600" />
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-slate-900">
                  {c.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">{c.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <div className="rounded-2xl bg-white p-10 shadow-soft md:p-14">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              By the numbers
            </p>
            <h2 data-testid="stats-heading" className="max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
              What usually happens.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
              A lawyer sends a demand letter. Many small owners pay $5,000 to $20,000
              to settle, then still have to fix the site. If it becomes a filed case,
              costs go up. Paying the letter does not stop the next lawyer.
            </p>
            <div className="mt-12 grid grid-cols-1 gap-10 border-t border-slate-200 pt-10 md:grid-cols-3">
              {[
                {
                  value: "3,000+",
                  label: "Federal website cases in 2025 — and 2026 is on pace to be higher",
                },
                {
                  value: "$5k–$20k",
                  label: "What many small owners pay to settle a demand letter, before fixing the site",
                },
                {
                  value: "NY · FL · CA · IL",
                  label: "The states that see the most of these cases",
                },
              ].map((s, i) => (
                <Reveal key={s.value} delay={i * 0.1}>
                  <div data-testid={`stat-${i}`}>
                    <p className="font-serif text-4xl font-medium tracking-tight text-slate-900 md:text-5xl">
                      {s.value}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section ref={imgRef} className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="relative h-[55vh] overflow-hidden rounded-2xl shadow-lift md:h-[70vh]">
          <motion.img
            src={HERO_IMG}
            alt="Business owner reviewing their website on a laptop"
            data-testid="parallax-hero-image"
            style={{ y }}
            className="absolute inset-0 h-[116%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 rounded-full bg-white/90 px-5 py-2.5 text-xs font-semibold text-slate-700 backdrop-blur-sm">
            Structure, examined — Fig. 01
          </div>
          <div className="absolute right-6 top-6 rounded-full bg-white/90 px-5 py-2.5 text-xs font-semibold text-slate-700 backdrop-blur-sm">
            8–15 Pages Audited
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <div className="grid grid-cols-1 items-center gap-12 rounded-2xl bg-slate-900 p-10 text-white shadow-lift md:grid-cols-2 md:p-16">
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-400">
                The Offer
              </p>
              <h2 data-testid="offer-heading" className="font-serif text-4xl font-medium leading-[1.1] tracking-tight md:text-6xl">
                One report.
                <br />
                Flat <em className="text-blue-400">$99.</em>
                <br />
                3 days.
              </h2>
            </div>
            <div>
              <p className="mb-8 text-sm leading-relaxed text-slate-300 md:text-base">
                No subscriptions. No overlays. No scare tactics. Pay once, send us your
                site address, and receive a written English report you keep forever.
              </p>
              <Link
                to="/report"
                data-testid="offer-cta-button"
                className="group inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-semibold text-slate-900 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-50 hover:shadow-md"
              >
                See What's Inside
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
